// ── participant-remove-modal: the remove-from-cohort confirm Modal (DELETE) ──
// Wave 4 (Participants). F6: removing a participant from a cohort is a DELETE
// /cohorts/:cohort_id/participants/:id; the server cancels future registrants +
// calendar events via the outbox. This modal is a deliberate confirmation step
// so a removal is never a one-click accident; the page owns the actual DELETE
// and toasts ONLY after the backend confirms (ok:true). A refused / closed write
// reads as its honest message inline and the dialog stays open — a closed write
// NEVER implies the participant was removed.

import { Alert, Button, Modal } from "@vjal/ui";

import { destructiveButtonClass } from "../../components/ui-classes";
import type { CoachingParticipantDto } from "../../services/coaching-dtos";
import { describeParticipantWriteError, participantDisplayName } from "./participants-helpers";

export interface ParticipantRemoveModalProps {
  open: boolean;
  participant: CoachingParticipantDto | null;
  onClose: () => void;
  /** The actual remove DELETE — owned by the page (react-query mutation). */
  onConfirm: () => Promise<unknown>;
  submitting?: boolean;
  submitError?: unknown;
}

export function ParticipantRemoveModal({
  open,
  participant,
  onClose,
  onConfirm,
  submitting = false,
  submitError
}: ParticipantRemoveModalProps) {
  const topError = describeParticipantWriteError(submitError);
  const name = participant ? participantDisplayName(participant.name, participant.email) : "this participant";
  const cohort = participant?.cohort_name?.trim() ? participant.cohort_name : "this cohort";

  return (
    <Modal open={open} onClose={onClose} title="Remove participant">
      <div className="flex flex-col gap-4">
        {topError && <Alert variant={topError.tone === "warning" ? "warning" : "error"}>{topError.message}</Alert>}

        <Alert variant="warning">
          Removing <span className="font-semibold">{name}</span> from{" "}
          <span className="font-semibold">{cohort}</span> cancels their future sessions and calendar invites. Past
          sessions and any reports are preserved.
        </Alert>

        <div className="mt-2 flex items-center justify-end gap-3">
          <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="danger"
            className={destructiveButtonClass}
            isLoading={submitting}
            onClick={() => {
              void onConfirm().catch(() => {
                // The owning mutation surfaces the error via `submitError`;
                // swallow so the dialog stays open with the inline error.
              });
            }}
          >
            Remove participant
          </Button>
        </div>
      </div>
    </Modal>
  );
}
